import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useState } from 'react';
import { LINKS } from '@/constants/links';
import { Button } from '@/components/buttons';
import { useTranslation } from 'react-i18next';
import { CalculatorForm } from './CalculatorForm';
import { Modal } from '@/components/modal';
import { formatAmount } from '@/utils/formatting';
import { Language } from '@/constants/currencyConfig';
import { SectionHeadline } from '@/components/SectionHeadline';
import { useCalculator } from './useCalculator';
import { formatApiAmount } from '@/utils/amount';
import { CalculatorResultRow } from '@/components/CalculatorResultRow';
import { mergeClasses } from '@/utils/style';
import type { ActionProps } from '@/types/actionProps';
const PRINCIPAL_MAX = 100_000;
const PRINCIPAL_MIN = 1_500;
const DEFAULT_SELECTED_PRINCIPAL = 50_000;
export type CalculatorType = 'installments' | 'loan';
interface CalculatorProps {
  compact?: boolean;
  registrationLink?: NonNullable<ActionProps['to']>;
  type?: CalculatorType;
}
export function Calculator(props) {
  const $ = _c(119);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [maxPrincipal, setMaxPrincipal] = useState(DEFAULT_SELECTED_PRINCIPAL);
  const t0 = props.type === "loan" ? "loan" : "installments";
  let t1;
  if ($[0] !== t0) {
    t1 = [t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    t,
    i18n
  } = useTranslation(t1);
  const [paymentsCount, setPaymentsCount] = useState(10);
  const calculatorId = props.type === "loan" ? "loanCalculator" : "installmentsCalculator";
  const registrationId = props.type === "loan" && props.compact ? "loansHeroPrimaryCTA" : `${calculatorId}Registration`;
  const exampleId = `${calculatorId}Example`;
  let t2;
  if ($[2] !== props.registrationLink) {
    t2 = props.registrationLink ?? LINKS().registration;
    $[2] = props.registrationLink;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const registrationLink = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const {
        maxPrincipal: selectedPrincipal,
        paymentsCount: selectedPaymentsCount
      } = t4;
      setMaxPrincipal(selectedPrincipal);
      setPaymentsCount(selectedPaymentsCount);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const calculate = t3;
  const calculator = useCalculator(maxPrincipal * 100, paymentsCount);
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setIsModalOpened(false);
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("calculator.example.title");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t5) {
    t6 = <h2 className="mb-6 text-xl font-semibold">{t5}</h2>;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("calculator.example.text.1");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <p className="mb-4 text-sm sm:text-base">{t7}</p>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t) {
    t9 = t("calculator.example.subtitle");
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t9) {
    t10 = <h3 className="mb-1 text-sm font-semibold sm:text-base">{t9}</h3>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t) {
    t11 = t("calculator.example.text.2");
    $[18] = t;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11) {
    t12 = <p className="mb-4 text-sm sm:text-base">{t11}</p>;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== exampleId || $[23] !== isModalOpened || $[24] !== t10 || $[25] !== t12 || $[26] !== t6 || $[27] !== t8) {
    t13 = <Modal isVisible={isModalOpened} onClose={t4} modalName={exampleId} analyticsParam={exampleId} testId={exampleId} className="max-w-[48rem] rounded-4xl p-10">{t6}{t8}{t10}{t12}</Modal>;
    $[22] = exampleId;
    $[23] = isModalOpened;
    $[24] = t10;
    $[25] = t12;
    $[26] = t6;
    $[27] = t8;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const t14 = props.compact && "px-0";
  let t15;
  if ($[29] !== t14) {
    t15 = mergeClasses("relative z-10 px-2", t14);
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== props.compact || $[32] !== props.type || $[33] !== t) {
    t16 = !props.compact && <SectionHeadline className="mb-10 px-4 md:mb-16" variant="inverted" anchorKey={`${props.type === "loan" ? "loan" : "installments"}.calculator.title`}>{t("calculator.title")}</SectionHeadline>;
    $[31] = props.compact;
    $[32] = props.type;
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = props.compact && "max-w-[38.5rem] md:p-10 xl:p-12";
  let t18;
  if ($[35] !== t17) {
    t18 = mergeClasses("mx-auto max-w-[60.5rem] rounded-2xl bg-blue-100 p-4 text-gray-900 md:rounded-[2.5rem] md:p-12 md:pt-14", t17);
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== calculator.installments || $[38] !== calculator.isLoading || $[39] !== props.type) {
    t19 = <CalculatorForm type={props.type} principalMax={PRINCIPAL_MAX} principalMin={PRINCIPAL_MIN} onChange={calculate} installments={calculator.installments} isLoading={calculator.isLoading} />;
    $[37] = calculator.installments;
    $[38] = calculator.isLoading;
    $[39] = props.type;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const t20 = props.compact && "md:mb-10";
  let t21;
  if ($[41] !== t20) {
    t21 = mergeClasses("mt-12 mb-4 flex flex-col items-center justify-between gap-3 rounded-2xl bg-blue-500 p-6 text-white md:mb-12", t20);
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t) {
    t22 = t("calculator.table.monthly");
    $[43] = t;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== calculator.selectedPlan?.installmentValue?.amount || $[46] !== i18n.language) {
    t23 = formatAmount(formatApiAmount(calculator?.selectedPlan?.installmentValue?.amount || 0), i18n.language as Language);
    $[45] = calculator.selectedPlan?.installmentValue?.amount;
    $[46] = i18n.language;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t22 || $[49] !== t23) {
    t24 = <CalculatorResultRow highlighted={true} title={t22} value={t23} />;
    $[48] = t22;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t) {
    t25 = t("calculator.table.plusYouPay");
    $[51] = t;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== calculator.selectedPlan?.totalInterestValue?.amount || $[54] !== i18n.language) {
    t26 = formatAmount(formatApiAmount(calculator?.selectedPlan?.totalInterestValue?.amount || 0), i18n.language as Language);
    $[53] = calculator.selectedPlan?.totalInterestValue?.amount;
    $[54] = i18n.language;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== t25 || $[57] !== t26) {
    t27 = <CalculatorResultRow highlighted={true} title={t25} value={t26} />;
    $[56] = t25;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t) {
    t28 = t("calculator.table.inTotal");
    $[59] = t;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== calculator.selectedPlan?.totalClaim?.amount || $[62] !== i18n.language) {
    t29 = formatAmount(formatApiAmount(calculator?.selectedPlan?.totalClaim?.amount || 0), i18n.language as Language);
    $[61] = calculator.selectedPlan?.totalClaim?.amount;
    $[62] = i18n.language;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t28 || $[65] !== t29) {
    t30 = <CalculatorResultRow highlighted={true} title={t28} value={t29} />;
    $[64] = t28;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t) {
    t31 = t("calculator.table.annualInterestRate");
    $[67] = t;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  const t32 = `${calculator.selectedPlan?.interestRate || 0} %`;
  let t33;
  if ($[69] !== t31 || $[70] !== t32) {
    t33 = <CalculatorResultRow title={t31} value={t32} />;
    $[69] = t31;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t) {
    t34 = t("calculator.table.rpsn");
    $[72] = t;
    $[73] = t34;
  } else {
    t34 = $[73];
  }
  const t35 = `${calculator.selectedPlan?.rpsn || 0} %`;
  let t36;
  if ($[74] !== t34 || $[75] !== t35) {
    t36 = <CalculatorResultRow title={t34} value={t35} />;
    $[74] = t34;
    $[75] = t35;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  let t37;
  if ($[77] !== t) {
    t37 = t("calculator.cta");
    $[77] = t;
    $[78] = t37;
  } else {
    t37 = $[78];
  }
  let t38;
  if ($[79] !== registrationId || $[80] !== registrationLink || $[81] !== t37) {
    t38 = <Button to={registrationLink} testId={registrationId} analyticsParam={registrationId} size="lg" variant="primary" align="center" className="mt-6 mb-3 max-w-full md:hidden lg:mt-0">{t37}</Button>;
    $[79] = registrationId;
    $[80] = registrationLink;
    $[81] = t37;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== t21 || $[84] !== t24 || $[85] !== t27 || $[86] !== t30 || $[87] !== t33 || $[88] !== t36 || $[89] !== t38) {
    t39 = <div className={t21}>{t24}{t27}{t30}{t33}{t36}{t38}</div>;
    $[83] = t21;
    $[84] = t24;
    $[85] = t27;
    $[86] = t30;
    $[87] = t33;
    $[88] = t36;
    $[89] = t38;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  let t40;
  if ($[91] !== t) {
    t40 = t("calculator.cta");
    $[91] = t;
    $[92] = t40;
  } else {
    t40 = $[92];
  }
  let t41;
  if ($[93] !== registrationId || $[94] !== registrationLink || $[95] !== t40) {
    t41 = <Button to={registrationLink} testId={registrationId} analyticsParam={registrationId} size="lg" variant="primary" align="center" className="mb-3 hidden md:block">{t40}</Button>;
    $[93] = registrationId;
    $[94] = registrationLink;
    $[95] = t40;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => setIsModalOpened(true);
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== t) {
    t43 = t("calculator.example.cta");
    $[98] = t;
    $[99] = t43;
  } else {
    t43 = $[99];
  }
  let t44;
  if ($[100] !== exampleId || $[101] !== t43) {
    t44 = <div className="text-center"><Button variant="unstyled" onClick={t42} testId={exampleId} analyticsParam={exampleId} align="center" className="text-blue-500 underline">{t43}</Button></div>;
    $[100] = exampleId;
    $[101] = t43;
    $[102] = t44;
  } else {
    t44 = $[102];
  }
  let t45;
  if ($[103] !== t18 || $[104] !== t19 || $[105] !== t39 || $[106] !== t41 || $[107] !== t44) {
    t45 = <div className={t18}>{t19}{t39}{t41}{t44}</div>;
    $[103] = t18;
    $[104] = t19;
    $[105] = t39;
    $[106] = t41;
    $[107] = t44;
    $[108] = t45;
  } else {
    t45 = $[108];
  }
  let t46;
  if ($[109] !== t15 || $[110] !== t16 || $[111] !== t45) {
    t46 = <div className={t15}>{t16}{t45}</div>;
    $[109] = t15;
    $[110] = t16;
    $[111] = t45;
    $[112] = t46;
  } else {
    t46 = $[112];
  }
  let t47;
  if ($[113] !== props.compact) {
    t47 = !props.compact && <div className="bgCircle absolute top-[10%] left-2/3" />;
    $[113] = props.compact;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  let t48;
  if ($[115] !== t13 || $[116] !== t46 || $[117] !== t47) {
    t48 = <>{t13}{t46}{t47}</>;
    $[115] = t13;
    $[116] = t46;
    $[117] = t47;
    $[118] = t48;
  } else {
    t48 = $[118];
  }
  return t48;
}